import React, { useState, useEffect } from 'react';
import { Tag, Plus, Award, Percent, DollarSign, Users } from 'lucide-react';

export default function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [newPromoName, setNewPromoName] = useState('');
  const [newPromoType, setNewPromoType] = useState('CASHBACK_PERCENT');
  const [newPromoValue, setNewPromoValue] = useState('');
  const [newPromoTarget, setNewPromoTarget] = useState('ALL_CLIENTS');
  const [newPromoStartDate, setNewPromoStartDate] = useState('');
  const [newPromoEndDate, setNewPromoEndDate] = useState('');
  const [newPromoBudget, setNewPromoBudget] = useState('');
  const [newPromoMaxUsesPerUser, setNewPromoMaxUsesPerUser] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPromotions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/promotions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur de chargement des promotions');
      }

      const data = await response.json();
      setPromotions(data);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if (!newPromoName.trim() || !newPromoValue || !newPromoStartDate || !newPromoEndDate) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Construct date string matching ISO LocalDateTime on backend (e.g. YYYY-MM-DDTHH:MM:SS)
      const startDateIso = newPromoStartDate + ':00';
      const endDateIso = newPromoEndDate + ':00';

      const response = await fetch('/api/admin/promotions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newPromoName,
          type: newPromoType,
          value: parseFloat(newPromoValue),
          target: newPromoTarget,
          startDate: startDateIso,
          endDate: endDateIso,
          budgetCapCents: newPromoBudget ? parseInt(newPromoBudget) * 100 : null,
          maxUsesPerUser: newPromoMaxUsesPerUser ? parseInt(newPromoMaxUsesPerUser) : null
        })
      });

      if (!response.ok) {
        throw new Error('Impossible de créer la promotion');
      }

      // Reset form
      setNewPromoName('');
      setNewPromoType('CASHBACK_PERCENT');
      setNewPromoValue('');
      setNewPromoTarget('ALL_CLIENTS');
      setNewPromoStartDate('');
      setNewPromoEndDate('');
      setNewPromoBudget('');
      setNewPromoMaxUsesPerUser('');
      setFormOpen(false);
      
      await fetchPromotions();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (promoId, currentStatus) => {
    const nextStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/promotions/${promoId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: nextStatus })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du changement de statut');
      }

      await fetchPromotions();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement des promotions...</div>;
  }

  const getPromoIcon = (type) => {
    switch (type) {
      case 'CASHBACK_PERCENT': return Percent;
      case 'FIXED_CREDIT': return DollarSign;
      case 'REFERRAL': return Users;
      default: return Tag;
    }
  };

  const getPromoTargetText = (target) => {
    switch (target) {
      case 'ALL_CLIENTS': return 'Tous les clients';
      case 'NEW_CLIENTS': return 'Nouveaux inscrits';
      case 'SPECIFIC_ZONE': return 'Zone Spécifique';
      default: return target;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <span style={styles.subtitle}>Gérez vos campagnes marketing et crédits de cashback</span>
        <button onClick={() => setFormOpen(!formOpen)} style={styles.addBtn} className="btn-primary">
          <Plus size={16} />
          <span>Créer une Campagne</span>
        </button>
      </div>

      <div style={styles.mainGrid}>
        {/* Left: Promotions cards */}
        <div style={styles.cardsPanel}>
          {promotions.length === 0 ? (
            <div style={styles.emptyCardPanel} className="glass-card">
              <Tag size={40} color="var(--text-muted)" />
              <p style={{ marginTop: '12px' }}>Aucune campagne promotionnelle active ou configurée.</p>
            </div>
          ) : (
            <div style={styles.promoGrid}>
              {promotions.map((promo) => {
                const Icon = getPromoIcon(promo.type);
                const budgetCap = promo.budgetCapCents ? promo.budgetCapCents / 100 : null;
                const budgetUsed = promo.budgetUsedCents / 100;
                const percentUsed = budgetCap ? Math.min(100, (budgetUsed / budgetCap) * 100) : 0;

                return (
                  <div key={promo.id} style={styles.promoCard} className="glass-card">
                    <div style={styles.cardHeader}>
                      <div style={styles.iconContainer}>
                        <Icon size={18} color="var(--primary)" />
                      </div>
                      <h4 style={styles.promoTitle}>{promo.name}</h4>
                    </div>

                    <div style={styles.cardBody}>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Cible :</span>
                        <span style={styles.infoVal}>{getPromoTargetText(promo.target)}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Valeur :</span>
                        <span style={styles.infoVal}>
                          {promo.type === 'CASHBACK_PERCENT' ? `${promo.value}%` : `${promo.value} €`}
                        </span>
                      </div>
                      {promo.maxUsesPerUser !== null && (
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>Limite par client :</span>
                          <span style={styles.infoVal}>{promo.maxUsesPerUser} util.</span>
                        </div>
                      )}
                      
                      {/* Budget Tracker */}
                      {budgetCap !== null ? (
                        <div style={styles.budgetContainer}>
                          <div style={styles.budgetHeader}>
                            <span style={styles.budgetLabel}>Budget consommé :</span>
                            <span style={styles.budgetVal}>{budgetUsed.toFixed(2)}€ / {budgetCap.toFixed(2)}€</span>
                          </div>
                          <div style={styles.progressWrapper}>
                            <div style={{ 
                              ...styles.progressBar, 
                              width: `${percentUsed}%`,
                              backgroundColor: percentUsed > 90 ? 'var(--danger)' : percentUsed > 75 ? 'var(--warning)' : 'var(--success)'
                            }} />
                          </div>
                        </div>
                      ) : (
                        <div style={styles.budgetContainer}>
                          <span style={styles.budgetLabel}>Budget :</span>
                          <span style={styles.budgetVal}>Illimité (consommé : {budgetUsed.toFixed(2)}€)</span>
                        </div>
                      )}
                    </div>

                    <div style={styles.cardFooter}>
                      <span className={`status-badge ${promo.status === 'ACTIVE' ? 'success' : 'warning'}`}>
                        {promo.status}
                      </span>
                      <button 
                        onClick={() => handleToggleStatus(promo.id, promo.status)}
                        style={{
                          ...styles.statusBtn,
                          color: promo.status === 'ACTIVE' ? 'var(--warning)' : 'var(--success)'
                        }}
                      >
                        {promo.status === 'ACTIVE' ? 'Suspendre' : 'Activer'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Creator Form */}
        {formOpen && (
          <div style={styles.formPanel} className="glass-card">
            <div style={styles.formHeader}>
              <Award size={18} color="var(--primary)" />
              <h3 style={styles.formTitle}>Nouvelle Campagne</h3>
            </div>
            <form onSubmit={handleCreatePromo} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nom de la campagne</label>
                <input 
                  type="text" 
                  placeholder="Ex: Cashback Été 2026, Bienvenue client..." 
                  value={newPromoName}
                  onChange={(e) => setNewPromoName(e.target.value)}
                  className="input-field"
                  required
                />
              </div>

              <div style={styles.inputRow}>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Type de promo</label>
                  <select 
                    value={newPromoType}
                    onChange={(e) => setNewPromoType(e.target.value)}
                    className="input-field"
                  >
                    <option value="CASHBACK_PERCENT">Cashback %</option>
                    <option value="FIXED_CREDIT">Crédit Fixe</option>
                    <option value="REFERRAL">Parrainage</option>
                  </select>
                </div>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Valeur (Crédit/%)</label>
                  <input 
                    type="number" 
                    placeholder="Ex: 10, 5" 
                    value={newPromoValue}
                    onChange={(e) => setNewPromoValue(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Cible marketing</label>
                <select 
                  value={newPromoTarget}
                  onChange={(e) => setNewPromoTarget(e.target.value)}
                  className="input-field"
                >
                  <option value="ALL_CLIENTS">Tous les clients</option>
                  <option value="NEW_CLIENTS">Nouveaux clients uniquement</option>
                </select>
              </div>

              <div style={styles.inputRow}>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Début</label>
                  <input 
                    type="datetime-local" 
                    value={newPromoStartDate}
                    onChange={(e) => setNewPromoStartDate(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Fin</label>
                  <input 
                    type="datetime-local" 
                    value={newPromoEndDate}
                    onChange={(e) => setNewPromoEndDate(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div style={styles.inputRow}>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Plafond Budget (EUR)</label>
                  <input 
                    type="number" 
                    placeholder="Illimité" 
                    value={newPromoBudget}
                    onChange={(e) => setNewPromoBudget(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div style={{ ...styles.inputGroup, flex: 1 }}>
                  <label style={styles.label}>Limite par client</label>
                  <input 
                    type="number" 
                    placeholder="Illimité" 
                    value={newPromoMaxUsesPerUser}
                    onChange={(e) => setNewPromoMaxUsesPerUser(e.target.value)}
                    className="input-field"
                    min="1"
                  />
                </div>
              </div>

              <div style={styles.formActions}>
                <button 
                  type="button" 
                  onClick={() => setFormOpen(false)} 
                  className="btn-secondary"
                  style={styles.cancelBtn}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={actionLoading}
                  className="btn-primary"
                  style={styles.submitBtn}
                >
                  Créer la campagne
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
  },
  addBtn: {
    padding: '10px 18px',
  },
  mainGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
  cardsPanel: {
    flex: 2,
    minWidth: '350px',
  },
  emptyCardPanel: {
    padding: '60px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
  },
  promoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  promoCard: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
  },
  promoTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flex: 1,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
  },
  infoLabel: {
    color: 'var(--text-secondary)',
  },
  infoVal: {
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  budgetContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '6px',
  },
  budgetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
  },
  budgetLabel: {
    color: 'var(--text-secondary)',
  },
  budgetVal: {
    fontWeight: '600',
  },
  progressWrapper: {
    width: '100%',
    height: '6px',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '9999px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: '9999px',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid var(--border-color)',
    paddingTop: '12px',
  },
  statusBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  formPanel: {
    flex: 1,
    minWidth: '280px',
    padding: '24px',
    alignSelf: 'flex-start',
    animation: 'fadeIn 0.3s ease',
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px',
  },
  formTitle: {
    fontSize: '1rem',
    fontWeight: '600',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inputRow: {
    display: 'flex',
    gap: '12px',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '1px solid var(--border-color)',
    paddingTop: '16px',
    marginTop: '8px',
  },
  cancelBtn: {
    padding: '8px 16px',
  },
  submitBtn: {
    padding: '8px 16px',
  }
};
